import { ChessTermType, TermAction } from "@chesshotel/types";
import { Chess, swapColor, PAWN, } from "@chesshotel/chess";
import { exchangeEvaluation, getThreats, pieceValues, uci2move, } from "../util.js";
import { isValidMove } from "./util.js";
export function detectRemoveDefenderThreat(fen, moveStr) {
    const chess = new Chess(fen);
    const color = chess.turn();
    const opponentColor = swapColor(color);
    const moveFrom = moveStr.slice(0, 2);
    const moveTo = moveStr.slice(2, 4);
    chess.move(uci2move(moveStr));
    const threats = getThreats(chess.fen(), opponentColor);
    chess.undo();
    const threat = threats?.find((threat) => threat.from.square === moveTo);
    if (!threat) {
        return;
    }
    const possibleMoves = chess.moves({ verbose: true });
    let bestDefendedAttack = null;
    let bestDefendedAttackValue = -Infinity;
    for (const move of possibleMoves) {
        if (!move.captured)
            continue;
        if (move.to === threat.to.square)
            continue;
        if (move.from === moveFrom)
            continue;
        if (!chess.canDefend(move.to, threat.to.square, threat.to.color, true)) {
            continue;
        }
        const v = pieceValues[move.piece] ?? 0;
        if (v > bestDefendedAttackValue) {
            bestDefendedAttackValue = v;
            bestDefendedAttack = move;
        }
    }
    if (!bestDefendedAttack) {
        return;
    }
    const moveResult = chess.move(uci2move(moveStr));
    const movesByThreatenedPiece = chess.moves({ verbose: true });
    const defending = bestDefendedAttack;
    let defendable = false;
    for (const move of movesByThreatenedPiece) {
        if (move.from !== threat.to.square)
            continue;
        chess.move(move);
        const exchange1 = exchangeEvaluation(chess.fen(), move.to, {
            from: move.from,
        });
        const canNotBeCaptured = exchange1.gain <= 0;
        if (!canNotBeCaptured) {
            chess.undo();
            continue;
        }
        const exchange2 = exchangeEvaluation(chess.fen(), defending.to);
        const canDefend = exchange2.gain <= 0;
        chess.undo();
        if (canDefend) {
            defendable = true;
            break;
        }
    }
    if (defendable) {
        return null;
    }
    const defendedPiece = chess.getBoardPiece(defending.to);
    const mainPiece = chess.getBoardPiece(moveResult.to);
    if (!defendedPiece || !mainPiece) {
        return null;
    }
    const maxGain = pieceValues[defendedPiece.type];
    const arrows = [
        { from: threat.from.square, to: threat.to.square },
        { from: defending.from, to: defending.to },
    ];
    return {
        term: ChessTermType.REMOVE_DEFENDER,
        termAction: TermAction.PERFORM_THREAT,
        mainPiece,
        move: moveResult,
        arrows,
        translationKeys: { piece: defendedPiece.type },
        maxGain,
    };
}
export function detectRemoveDefender(fen, moveStr) {
    const chess = new Chess(fen);
    const color = chess.turn();
    const opponentColor = swapColor(color);
    const moveFrom = moveStr.slice(0, 2);
    const moveTo = moveStr.slice(2, 4);
    const capturedPiece = chess.getBoardPiece(moveTo);
    if (!capturedPiece) {
        return;
    }
    const exchange = exchangeEvaluation(fen, moveTo, {
        from: moveFrom,
    });
    const { gain, moveSequence } = exchange;
    if (moveSequence.length > 2) {
        return;
    }
    const possibleMoves = chess.moves({ verbose: true });
    const defendedPieces = [];
    for (const move of possibleMoves) {
        if (!move.captured)
            continue;
        if (move.to === moveTo)
            continue;
        if (move.piece === PAWN)
            continue;
        if (chess.canDefend(move.to, capturedPiece.square, capturedPiece.color, true)) {
            defendedPieces.push(move);
        }
    }
    if (defendedPieces.length === 0) {
        return;
    }
    const moveResult = chess.move(uci2move(moveStr));
    const fenAfterCapture = chess.fen();
    for (const move of defendedPieces) {
        if (!isValidMove(chess, { from: move.to, to: move.from })) {
            continue;
        }
        const { gain, moveSequence } = exchangeEvaluation(fenAfterCapture, move.from, { from: move.to });
        if (gain >= 0 && moveSequence.length > 0) {
            return null;
        }
    }
    if (chess.inCheck()) {
        return null;
    }
    chess.changeTurn();
    const fenAfterTurnSwap = chess.fen();
    const vulnerableDefendedPieces = [];
    for (const move of defendedPieces) {
        const { gain } = exchangeEvaluation(fenAfterTurnSwap, move.to, {
            from: move.from,
        });
        if (gain > 0) {
            vulnerableDefendedPieces.push({ move, gain });
        }
    }
    vulnerableDefendedPieces.sort((a, b) => b.gain - a.gain);
    if (vulnerableDefendedPieces.length > 0) {
        const totalGain = gain + vulnerableDefendedPieces[0].gain;
        if (totalGain <= 0) {
            return;
        }
        const mainPiece = chess.getBoardPiece(moveResult.to);
        const captureMoves = vulnerableDefendedPieces.map(({ move }) => move);
        const captureMove = captureMoves[0];
        const voulnerableDefendedPiece = chess.getBoardPiece(captureMove.to);
        if (!voulnerableDefendedPiece || !mainPiece) {
            return null;
        }
        const arrows = captureMoves;
        const translationKeys = {
            piece: voulnerableDefendedPiece.type,
        };
        return {
            term: ChessTermType.REMOVE_DEFENDER,
            mainPiece,
            capturedPiece,
            move: moveResult,
            arrows,
            translationKeys,
            maxGain: vulnerableDefendedPieces[0].gain,
        };
    }
}
//# sourceMappingURL=removeDefender.js.map